import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { useI18n } from '@affine/i18n';
import { FolderIcon, GroupIcon, UserIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';

import { SettingsOfflineHeaderAction } from '../../shared/settings-offline-status';
import { SettingsEntityMessage } from '../shared/settings-entity';
import { SettingsSearchableEntityPage } from '../shared/settings-entity-page';
import { SettingsViewModeMenu } from '../shared/settings-navigation';
import { FolderListView } from './folder-list-view';
import { FolderGroupListView } from './group-list-view';
import { UserListView } from './user-list-view';

type ViewMode = 'by-folder' | 'by-user' | 'by-group';

export const FoldersSettingsPanel = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const permissionService = useService(WorkspacePermissionService);

  const isOwner = useLiveData(permissionService.permission.isOwner$);
  const isAdmin = useLiveData(permissionService.permission.isAdmin$);
  const canManage = isOwner || isAdmin;

  const [viewMode, setViewMode] = useState<ViewMode>('by-folder');
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const viewModeLabel = useMemo(() => {
    switch (viewMode) {
      case 'by-folder':
        return (
          t['com.streamlain.settings.workspace.folders.by-folder']?.() ??
          'By Folder'
        );
      case 'by-user':
        return (
          t['com.streamlain.settings.workspace.folders.by-user']?.() ??
          'By User'
        );
      case 'by-group':
        return (
          t['com.streamlain.settings.workspace.folders.by-group']?.() ??
          'By Group'
        );
    }
  }, [viewMode, t]);

  const searchPlaceholder = useMemo(() => {
    switch (viewMode) {
      case 'by-folder':
        return (
          t['com.streamlain.settings.workspace.folders.search-folders']?.() ??
          'Search folders...'
        );
      case 'by-user':
        return (
          t['com.streamlain.settings.workspace.folders.search-users']?.() ??
          'Search users...'
        );
      case 'by-group':
        return (
          t['com.streamlain.settings.workspace.folders.search-groups']?.() ??
          'Search groups...'
        );
    }
  }, [viewMode, t]);

  if (!canManage) {
    return (
      <SettingsEntityMessage>
        {t['com.streamlain.settings.workspace.permissions.no-access']?.() ??
          'You need to be an Owner or Admin to manage permissions.'}
      </SettingsEntityMessage>
    );
  }

  return (
    <SettingsSearchableEntityPage
      title={
        t['com.streamlain.settings.workspace.folders.title']?.() ??
        'Folder Permissions'
      }
      subtitle={
        t['com.streamlain.settings.workspace.folders.description']?.() ??
        'Manage folder permissions for workspace members.'
      }
      headerAction={<SettingsOfflineHeaderAction show={!backendAvailable} />}
      searchValue={searchQuery}
      onSearchChange={handleSearchChange}
      searchPlaceholder={searchPlaceholder}
      action={
        <SettingsViewModeMenu
          activeId={viewMode}
          label={viewModeLabel}
          onChange={id => {
            setViewMode(id as ViewMode);
            setSearchQuery('');
          }}
          items={[
            {
              id: 'by-folder',
              label:
                t['com.streamlain.settings.workspace.folders.by-folder']?.() ??
                'By Folder',
              icon: <FolderIcon />,
            },
            {
              id: 'by-user',
              label:
                t['com.streamlain.settings.workspace.folders.by-user']?.() ??
                'By User',
              icon: <UserIcon />,
            },
            {
              id: 'by-group',
              label:
                t['com.streamlain.settings.workspace.folders.by-group']?.() ??
                'By Group',
              icon: <GroupIcon />,
            },
          ]}
        />
      }
      loading={false}
      hasItems={true}
      loadingContent={null}
      emptyContent={null}
    >
      {viewMode === 'by-folder' && <FolderListView searchQuery={searchQuery} />}
      {viewMode === 'by-user' && <UserListView searchQuery={searchQuery} />}
      {viewMode === 'by-group' && (
        <FolderGroupListView searchQuery={searchQuery} />
      )}
    </SettingsSearchableEntityPage>
  );
};
